import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AdaptationIcon,
  Schedule as ReassessmentIcon,
  Lightbulb as LessonIcon
} from '@mui/icons-material';

import useRoadmapStore from '../store/roadmapStore';
import { STEP_LABELS } from '../utils/constants';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import useAISuggest from '../hooks/useAISuggest';

export default function AdaptationPage() {
  const { t } = useTranslation();
  const handleAISuggest = useAISuggest('adaptation');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.adaptation);
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();

  const stepData = getStepData('adaptation');
  const [reassessmentPoints, setReassessmentPoints] = useState(stepData.reassessment_points || []);
  const [pivotGuidelines, setPivotGuidelines] = useState(stepData.pivot_guidelines || '');
  const [lessonsLearned, setLessonsLearned] = useState(stepData.lessons_learned || []);
  const [isComplete, setIsComplete] = useState(isStepCompleted('adaptation'));

  const [newReassessmentPoint, setNewReassessmentPoint] = useState({ date: '', milestone: '', criteria: '' });
  const [newLesson, setNewLesson] = useState({ lesson: '', context: '', action: '' });

  useEffect(() => {
    const data = getStepData('adaptation');
    setReassessmentPoints(data.reassessment_points || []);
    setPivotGuidelines(data.pivot_guidelines || '');
    setLessonsLearned(data.lessons_learned || []);
    setIsComplete(isStepCompleted('adaptation'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const updateData = () => {
    updateStepData('adaptation', {
      reassessment_points: reassessmentPoints,
      pivot_guidelines: pivotGuidelines,
      lessons_learned: lessonsLearned
    });
  };

  const handleAddReassessmentPoint = () => {
    if (newReassessmentPoint.date && newReassessmentPoint.milestone.trim()) {
      const updated = [...reassessmentPoints, newReassessmentPoint];
      setReassessmentPoints(updated);
      setNewReassessmentPoint({ date: '', milestone: '', criteria: '' });
      updateStepData('adaptation', {
        reassessment_points: updated,
        pivot_guidelines: pivotGuidelines,
        lessons_learned: lessonsLearned
      });
    }
  };

  const handleDeleteReassessmentPoint = (index) => {
    const updated = reassessmentPoints.filter((_, i) => i !== index);
    setReassessmentPoints(updated);
    updateStepData('adaptation', {
      reassessment_points: updated,
      pivot_guidelines: pivotGuidelines,
      lessons_learned: lessonsLearned
    });
  };

  const handleAddLesson = () => {
    if (newLesson.lesson.trim()) {
      const updated = [...lessonsLearned, newLesson];
      setLessonsLearned(updated);
      setNewLesson({ lesson: '', context: '', action: '' });
      updateStepData('adaptation', {
        reassessment_points: reassessmentPoints,
        pivot_guidelines: pivotGuidelines,
        lessons_learned: updated
      });
    }
  };

  const handleDeleteLesson = (index) => {
    const updated = lessonsLearned.filter((_, i) => i !== index);
    setLessonsLearned(updated);
    updateStepData('adaptation', {
      reassessment_points: reassessmentPoints,
      pivot_guidelines: pivotGuidelines,
      lessons_learned: updated
    });
  };

  const handleSavePivotGuidelines = () => {
    updateData();
  };

  const _saveRef = useRef(null);
  useEffect(() => { _saveRef.current = handleSavePivotGuidelines; });
  useEffect(() => () => { if (_saveRef.current) _saveRef.current(); }, []); // eslint-disable-line

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('adaptation', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = reassessmentPoints.length > 0;

  return (
    <PageContainer maxWidth={1200}>
      <StepPageHeader
        stepNum={10}
        title={t(STEP_LABELS.adaptation)}
        intro={t('stepContent.adaptation.intro')}
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        requireMessage={t('stepContent.adaptation.alerts.requireReassessment')}
      onAISuggest={handleAISuggest}
        />

      {/* Reassessment Points */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReassessmentIcon />
          {t('stepContent.adaptation.sections.reassessment.title')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('stepContent.adaptation.sections.reassessment.description')}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.adaptation.form.reassessment.fields.date')}
              type="date"
              value={newReassessmentPoint.date}
              onChange={(e) => setNewReassessmentPoint({ ...newReassessmentPoint, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.adaptation.form.reassessment.fields.milestone')}
              value={newReassessmentPoint.milestone}
              onChange={(e) => setNewReassessmentPoint({ ...newReassessmentPoint, milestone: e.target.value })}
              placeholder={t('stepContent.adaptation.form.reassessment.placeholders.milestone')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.adaptation.form.reassessment.fields.criteria')}
              value={newReassessmentPoint.criteria}
              onChange={(e) => setNewReassessmentPoint({ ...newReassessmentPoint, criteria: e.target.value })}
              placeholder={t('stepContent.adaptation.form.reassessment.placeholders.criteria')}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddReassessmentPoint}
              disabled={!newReassessmentPoint.date || !newReassessmentPoint.milestone.trim()}
              sx={{ height: '40px' }}
            >
              <AddIcon />
            </Button>
          </Grid>
        </Grid>

        <List>
          {reassessmentPoints.map((point, index) => (
            <ListItem
              key={index}
              sx={{ bgcolor: 'grey.50', mb: 1, borderRadius: 1 }}
              secondaryAction={
                <IconButton onClick={() => handleDeleteReassessmentPoint(index)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemIcon>
                <ReassessmentIcon />
              </ListItemIcon>
              <ListItemText
                primary={`${point.date} - ${point.milestone}`}
                secondary={point.criteria}
              />
            </ListItem>
          ))}
        </List>

        {reassessmentPoints.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            {t('stepContent.adaptation.empty.reassessment')}
          </Typography>
        )}
      </Paper>

      {/* Pivot Guidelines */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdaptationIcon />
          {t('stepContent.adaptation.sections.pivot.title')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('stepContent.adaptation.sections.pivot.description')}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={8}
          label={t('stepContent.adaptation.form.pivot.label')}
          value={pivotGuidelines}
          onChange={(e) => setPivotGuidelines(e.target.value)}
          onBlur={handleSavePivotGuidelines}
          placeholder={t('stepContent.adaptation.form.pivot.placeholder')}
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          onClick={handleSavePivotGuidelines}
        >
          {t('stepContent.adaptation.actions.savePivot')}
        </Button>
      </Paper>

      {/* Lessons Learned */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LessonIcon />
          {t('stepContent.adaptation.sections.lessons.title')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('stepContent.adaptation.sections.lessons.description')}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.adaptation.form.lessons.fields.lesson')}
              value={newLesson.lesson}
              onChange={(e) => setNewLesson({ ...newLesson, lesson: e.target.value })}
              placeholder={t('stepContent.adaptation.form.lessons.placeholders.lesson')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.adaptation.form.lessons.fields.context')}
              value={newLesson.context}
              onChange={(e) => setNewLesson({ ...newLesson, context: e.target.value })}
              placeholder={t('stepContent.adaptation.form.lessons.placeholders.context')}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.adaptation.form.lessons.fields.action')}
              value={newLesson.action}
              onChange={(e) => setNewLesson({ ...newLesson, action: e.target.value })}
              placeholder={t('stepContent.adaptation.form.lessons.placeholders.action')}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddLesson}
              disabled={!newLesson.lesson.trim()}
              sx={{ height: '40px' }}
            >
              <AddIcon />
            </Button>
          </Grid>
        </Grid>

        <List>
          {lessonsLearned.map((lesson, index) => (
            <ListItem
              key={index}
              sx={{ bgcolor: 'grey.50', mb: 1, borderRadius: 1 }}
              secondaryAction={
                <IconButton onClick={() => handleDeleteLesson(index)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemIcon>
                <LessonIcon />
              </ListItemIcon>
              <ListItemText
                primary={lesson.lesson}
                secondary={`Context: ${lesson.context || 'Not specified'} | Action: ${lesson.action || 'Not specified'}`}
              />
            </ListItem>
          ))}
        </List>

        {lessonsLearned.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            {t('stepContent.adaptation.empty.lessons')}
          </Typography>
        )}
      </Paper>
    </PageContainer>
  );
}
